import { useState } from 'react'
import { useMutation } from '@apollo/client'
import { matchPath } from 'react-router'
import { useLocation } from 'react-router-dom'
import { Button } from '@winnin/winnin_ui.components.button'
import { Checkbox } from '@winnin/winnin_ui.components.checkbox'
import { Modal } from '@winnin/winnin_ui.components.modal'
import { Title } from '@winnin/winnin_ui.components.title'
import { useToast } from '@winnin/winnin_ui.components.toast'

import { SMALL_MODAL } from 'consts/modalStyles'
import { useSelectedItemsList } from 'contexts/SelectedItemsListProvider'
import { T, useTranslate } from 'lib/tolgee'
import { getPinChangesFromItems, updateWorkspaceJobPinsCache } from 'modules/dashboard/common/dashboard.utils'
import { TOLGEE_GLOBALS } from 'utils'

import { REMOVE_FROM_JOB } from './query'
import UndoRemoveToast from '../UndoRemoveToast'

import './index.scss'

const modalStyle = {
  ...SMALL_MODAL,
  content: {
    ...SMALL_MODAL.content,
    padding: '32px',
  },
}

const TOLGEE_PREFIX = 'RemoveFromJobModal'
const checkboxLabelKey = `${TOLGEE_PREFIX}-checkboxLabel`
const subtitleJobKey = `${TOLGEE_PREFIX}-subtitleJob`
const titleJobKey = `${TOLGEE_PREFIX}-titleJob`

export default function RemoveFromJobModal({ isOpen, closeModal }) {
  const [isCheckboxActive, setIsCheckboxActive] = useState(false)
  const [temporaryRemoveData, setTemporaryRemoveData] = useState(null)

  const { selectedItemsBar, setSelectedItemsBar } = useSelectedItemsList()
  const { createToast } = useToast()

  // Using matchPath to get the folder and jobId from the URL since this component is not a direct child of the route
  const { pathname } = useLocation()
  // This path should be the same as used in the route
  const match = matchPath(pathname, { path: '/workspace/:panelId/jobs/:jobId/:tab/:folder?/:subTab?' })
  const { folder, jobId } = match?.params || {}

  const t = useTranslate()

  const getTolgeeKey = key => `${key}-asDashboard`

  const isAllReferencesFolder = folder === 'allReferences'

  const [removeFromJob] = useMutation(REMOVE_FROM_JOB, {
    onCompleted: data => {
      if (data?.deleteWorkspaceJobPins?.success) {
        const removedData = {
          ...temporaryRemoveData,
          isCheckboxActive,
          pinsList: [...selectedItemsBar],
        }
        createToast({
          content: <UndoRemoveToast removedData={removedData} />,
        })
        setSelectedItemsBar([])
        closeModal()
        setTemporaryRemoveData(null)
      }
    },
    onError: error => console.error('error @REMOVE_FROM_JOB >>>>', error),
    refetchQueries: [
      'getWorkspaceJob',
      'getJobCretors',
      'getJobTopics',
      'getJobVideos',
      'getFolder',
      'getJobData',
      'getJobAllEntities',
    ],
    update: (cache, { data }) => {
      if (!data?.deleteWorkspaceJobPins?.success || !jobId) return

      const shouldUpdateJobCache = isCheckboxActive || isAllReferencesFolder

      if (shouldUpdateJobCache) {
        const pinChanges = getPinChangesFromItems(selectedItemsBar)
        updateWorkspaceJobPinsCache(cache, jobId, pinChanges, 'remove')
      }
    },
  })

  const handleCheckbox = () => {
    setIsCheckboxActive(prevState => !prevState)
  }

  const handleRemove = () => {
    if (!jobId || !folder) {
      createToast({
        content: t(TOLGEE_GLOBALS.somethingWentWrong),
      })
      return
    }

    const folderIds = isAllReferencesFolder ? [] : [folder]

    const deleteWorkspaceJobPinInput = selectedItemsBar
      .map(item => ({
        id: String(item?.pinId),
        folders: folderIds,
        deleteFromJob: isCheckboxActive || isAllReferencesFolder,
      }))
      .filter(item => item?.id)

    setTemporaryRemoveData({ jobId, folderIds })

    removeFromJob({
      variables: {
        deleteWorkspaceJobPinInput,
        workspaceJobId: jobId,
      },
    })
  }

  return (
    <Modal isOpen={isOpen} onRequestClose={closeModal} style={modalStyle}>
      <div className="RemoveFromJobModal-wrapper">
        <Title tag="h3">
          {folder === 'allReferences' ? (
            <T keyName={getTolgeeKey(titleJobKey)}>Remover referências do dashboard?</T>
          ) : (
            <T keyName={`${TOLGEE_PREFIX}-title`}>Remover referências da pasta?</T>
          )}
        </Title>
        <p>
          {folder === 'allReferences' ? (
            <T keyName={getTolgeeKey(subtitleJobKey)}>
              As referências selecionadas serão completamente removidas deste dashboard
            </T>
          ) : (
            <T keyName={`${TOLGEE_PREFIX}-subtitleFolder`}>
              As referências selecionadas serão removidas desta pasta. Caso deseje recuperá-las, será possível
              encontrá-las em ‘Todas as referências’.
            </T>
          )}
        </p>
        {!isAllReferencesFolder ? (
          <Checkbox isActive={isCheckboxActive} handleClick={handleCheckbox} isHoverActive>
            <T keyName={getTolgeeKey(checkboxLabelKey)}>Remover também de todo o dashboard</T>
          </Checkbox>
        ) : null}
        <div className="buttonsSection">
          <Button appearance="link" onClick={closeModal}>
            <T keyName={TOLGEE_GLOBALS.cancel.key}>{TOLGEE_GLOBALS.cancel.defaultValue}</T>
          </Button>
          <Button onClick={handleRemove}>
            <T keyName={TOLGEE_GLOBALS.remove.key}>{TOLGEE_GLOBALS.remove.defaultValue}</T>
          </Button>
        </div>
      </div>
    </Modal>
  )
}
